/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, Fragment, useState } from "react";
import { Grid, makeStyles, createStyles } from "@material-ui/core";
import { GridSize } from "@material-ui/core/Grid";
import { get } from "lodash";
import { ChangeNames, formModelToChangeSet } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { LabledTextField } from "../core/LabledTextField";
import { Select, MenuItem } from "../core/Select";
import { Button } from "../core/Button";
import { Colors } from "../../styles";
import InputLabel from "@material-ui/core/InputLabel";
import { AddressCompletionField } from "../core/AddressCompletionField";

const useStyles = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            padding: 0,
            borderRadius: 8,
            marginTop: 0,
            marginBottom: 24,
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        title: {
            margin: 0,
            fontSize: 37,
            fontWeight: 500,
            height: 43,
            marginBottom: 24,
        },
        subTitle: {
            margin: 0,
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.45,
            height: 32,
            marginBottom: 8,
        },
        text: {
            margin: 0,
            fontSize: 22,
            fontWeight: 300,
            lineHeight: 1.27,
            marginBottom: 24,
        },
        labledTextfieldLabel: {
            marginLeft: 20,
            height: 28,
            fontSize: 22,
            fontWeight: 300,
            fontStretch: "normal",
            fontStyle: "normal",
            fontFamily: "innogyWEB",
            lineHeight: 1.27,
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        disclaimerContainer: {
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite50,
            fontSize: 18,
        },
    }),
);

type PathProxyField = {
    key: string;
    label: string;
    path?: string;
    width: number;
};

type SalutationField = {
    label: string;
    width: number;
};

type FillerField = {
    width: number;
};

type Field = {
    pathProxyField?: PathProxyField;
    salutationField?: SalutationField;
    fillerField?: FillerField;
};

type Fields = {
    [x: string]: Field;
};

type RenderFields = {
    [x: string]: Field;
    natural: Fields;
    legal: Fields;
};

type Salutation = {
    key: string;
    label: string;
    type: "natural" | "legal";
};

const salutations: Salutation[] = [
    { key: "mr", label: "Herr", type: "natural" },
    { key: "ms", label: "Frau", type: "natural" },
    { key: "diverse", label: "Divers", type: "natural" },
    { key: "family", label: "Familie", type: "natural" },
    { key: "spouses", label: "Eheleute", type: "natural" },
    { key: "company", label: "Firma", type: "legal" },
    { key: "flatSharingCommunity", label: "Wohngemeinschaft", type: "legal" },
    { key: "buildingOwnerAssociation", label: "Bauherrengemeinschaft", type: "legal" },
    { key: "heirCommunity", label: "Erbengemeinschaft", type: "legal" },
    { key: "publicOfficials", label: "Träger öffentlicher Belange", type: "legal" },
];

export const InvoiceRecipientForm: FC = () => {
    const classes = useStyles();

    const { getFieldProps, getFieldValue, setFieldValue, values, setValues } =
        useFormModel<ChangeNames.INVOICE_RECIPIENT>();
    const [isInitialized, setIsInitialized] = useState(false);

    const naturalFields: Fields = {
        vorname: {
            pathProxyField: {
                key: "Vorname",
                label: "Vorname *",
                path: "invoiceRecipient.value.natural.firstName",
                width: 6,
            },
        },
        nachname: {
            pathProxyField: {
                key: "Nachname",
                label: "Nachname *",
                path: "invoiceRecipient.value.natural.lastName",
                width: 6,
            },
        },
        plz: {
            pathProxyField: {
                key: "PLZ",
                label: "PLZ *",
                path: "invoiceRecipient.value.natural.address.zipCode",
                width: 6,
            },
        },
        ort: {
            pathProxyField: {
                key: "Ort",
                label: "Ort *",
                path: "invoiceRecipient.value.natural.address.city",
                width: 6,
            },
        },
        strasse: {
            pathProxyField: {
                key: "Strasse",
                label: "Straße *",
                path: "invoiceRecipient.value.natural.address.street",
                width: 6,
            },
        },
        hausnummer: {
            pathProxyField: {
                key: "Hausnummer",
                label: "Hausnummer *",
                path: "invoiceRecipient.value.natural.address.houseNumber",
                width: 6,
            },
        },
        email: {
            pathProxyField: {
                key: "Email",
                label: "E-Mail-Adresse*",
                path: "invoiceRecipient.value.natural.email",
                width: 6,
            },
        },
        telefon: {
            pathProxyField: {
                key: "Telefon",
                label: "Telefonnummer *",
                path: "invoiceRecipient.value.natural.phoneNumber",
                width: 6,
            },
        },
    };

    const legalFields: Fields = {
        name: {
            pathProxyField: {
                key: "Name",
                label: "Name *",
                path: "invoiceRecipient.value.legal.name",
                width: 12,
            },
        },
        plz: {
            pathProxyField: {
                key: "PLZ",
                label: "PLZ *",
                path: "invoiceRecipient.value.legal.address.zipCode",
                width: 6,
            },
        },
        ort: {
            pathProxyField: {
                key: "Ort",
                label: "Ort *",
                path: "invoiceRecipient.value.legal.address.city",
                width: 6,
            },
        },
        strasse: {
            pathProxyField: {
                key: "Strasse",
                label: "Straße *",
                path: "invoiceRecipient.value.legal.address.street",
                width: 6,
            },
        },
        hausnummer: {
            pathProxyField: {
                key: "Hausnummer",
                label: "Hausnummer *",
                path: "invoiceRecipient.value.legal.address.houseNumber",
                width: 6,
            },
        },
        email: {
            pathProxyField: {
                key: "Email",
                label: "E-Mail-Adresse*",
                path: "invoiceRecipient.value.legal.email",
                width: 6,
            },
        },
        telefon: {
            pathProxyField: {
                key: "Telefon",
                label: "Telefonnummer *",
                path: "invoiceRecipient.value.legal.phoneNumber",
                width: 6,
            },
        },
    };

    const fields: RenderFields = {
        salutation: {
            salutationField: {
                label: "Anrede *",
                width: 6,
            },
        },
        salutationFiller: {
            fillerField: { width: 6 },
        },
        natural: naturalFields,
        legal: legalFields,
    };

    const getSalutationFromChangeSet = (): string => {
        const changeSet = formModelToChangeSet(values);
        const invoiceRecipient = changeSet.invoiceRecipient;

        if (!invoiceRecipient) return "";

        const salutation = (
            Object.keys(invoiceRecipient.salutation) as Array<
                keyof typeof invoiceRecipient.salutation
            >
        ).find((k) => invoiceRecipient.salutation[k] === true);

        return salutation ? salutation : "";
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const isNaturalBySalutationValue = (salutationValue: string) =>
        salutations.find((salutation) => salutation.key === salutationValue)?.type === "natural";

    const [isNatural, setIsNatural] = useState(
        isNaturalBySalutationValue(getSalutationFromChangeSet()),
    );

    /* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */
    const setSalutation = (newSalutation: string) => {
        const newIsNatural = newSalutation === "" || isNaturalBySalutationValue(newSalutation);

        const legalPath = "invoiceRecipient.value.legal";
        const legalSubtree = get(values, legalPath);

        const naturalPath = "invoiceRecipient.value.natural";
        const naturalSubtree = get(values, naturalPath);

        setValues((values) => {
            // Salutation Booleans auf das neue Pattern setzen. (true der Ausgewählte, alle anderen false)
            values.invoiceRecipient.value.salutation.mr = newSalutation === "mr";
            values.invoiceRecipient.value.salutation.ms = newSalutation === "ms";
            values.invoiceRecipient.value.salutation.diverse = newSalutation === "diverse";
            values.invoiceRecipient.value.salutation.family = newSalutation === "family";
            values.invoiceRecipient.value.salutation.spouses = newSalutation === "spouses";
            values.invoiceRecipient.value.salutation.company = newSalutation === "company";
            values.invoiceRecipient.value.salutation.flatSharingCommunity =
                newSalutation === "flatSharingCommunity";
            values.invoiceRecipient.value.salutation.buildingOwnerAssociation =
                newSalutation === "buildingOwnerAssociation";
            values.invoiceRecipient.value.salutation.heirCommunity =
                newSalutation === "heirCommunity";
            values.invoiceRecipient.value.salutation.publicOfficials =
                newSalutation === "publicOfficials";

            // Hat sich die Gruppe, also legal oder natural geändert?
            if (newIsNatural !== isNatural) {
                // Wenn vorher legal und jetzt natural, dann müssen wir für natural einen Kontainer anlegen
                if (newIsNatural && legalSubtree) {
                    if (!values.invoiceRecipient.value.natural) {
                        values.invoiceRecipient.value.natural = {
                            address: {
                                city: { value: "" },
                                houseNumber: { value: "" },
                                street: { value: "" },
                                zipCode: { value: "" },
                            },
                            email: { value: "" },
                            firstName: { value: "" },
                            lastName: { value: "" },
                            phoneNumber: { value: "" },
                        };
                    }
                    values.invoiceRecipient.value.natural.lastName = {
                        value: get(values, legalPath + ".name.value")!.substring(0, 35),
                    };
                    values.invoiceRecipient.value.natural.email = {
                        value: get(values, legalPath + ".email.value") || null,
                    };
                    values.invoiceRecipient.value.natural.phoneNumber = {
                        value: get(values, legalPath + ".phoneNumber.value") || null,
                    };
                    values.invoiceRecipient.value.natural.address.street = {
                        value: get(values, legalPath + ".address.street.value") || "",
                    };
                    values.invoiceRecipient.value.natural.address.houseNumber = {
                        value: get(values, legalPath + ".address.houseNumber.value") || "",
                    };
                    values.invoiceRecipient.value.natural.address.zipCode = {
                        value: get(values, legalPath + ".address.zipCode.value") || "",
                    };
                    values.invoiceRecipient.value.natural.address.city = {
                        value: get(values, legalPath + ".address.city.value") || "",
                    };
                    delete values.invoiceRecipient.value.legal;
                }
                if (!newIsNatural && naturalSubtree) {
                    // Wenn vorher natural und jetzt legal, dann müssen wir für legal einen Kontainer anlegen
                    if (!values.invoiceRecipient.value.legal) {
                        values.invoiceRecipient.value.legal = {
                            address: {
                                city: { value: "" },
                                houseNumber: { value: "" },
                                street: { value: "" },
                                zipCode: { value: "" },
                            },
                            email: { value: "" },
                            name: { value: "" },
                            phoneNumber: { value: "" },
                        };
                    }
                    values.invoiceRecipient.value.legal.name = {
                        value: get(values, naturalPath + ".lastName.value") || "",
                    };
                    values.invoiceRecipient.value.legal.email = {
                        value: get(values, naturalPath + ".email.value") || null,
                    };
                    values.invoiceRecipient.value.legal.phoneNumber = {
                        value: get(values, naturalPath + ".phoneNumber.value") || null,
                    };
                    values.invoiceRecipient.value.legal.address.street = {
                        value: get(values, naturalPath + ".address.street.value") || "",
                    };
                    values.invoiceRecipient.value.legal.address.houseNumber = {
                        value: get(values, naturalPath + ".address.houseNumber.value") || "",
                    };
                    values.invoiceRecipient.value.legal.address.zipCode = {
                        value: get(values, naturalPath + ".address.zipCode.value") || "",
                    };
                    values.invoiceRecipient.value.legal.address.city = {
                        value: get(values, naturalPath + ".address.city.value") || "",
                    };
                    delete values.invoiceRecipient.value.natural;
                }
            }
        });
        setIsNatural(newIsNatural);
    };

    /* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */
    const onChangeSalutation = (e: any) => {
        setSalutation(e.target.value);
    };

    const renderField = (field: Field): JSX.Element => {
        const pathProxyField = field.pathProxyField;
        const fillerField = field.fillerField;
        const salutationField = field.salutationField;

        if (pathProxyField && pathProxyField.path) {
            if (["PLZ", "Ort", "Strasse"].includes(pathProxyField.key)) {
                const complete =
                    pathProxyField.key === "PLZ"
                        ? "zipCode"
                        : pathProxyField.key === "Ort"
                        ? "city"
                        : "street";
                const address = isNatural
                    ? values.invoiceRecipient.value.natural?.address
                    : values.invoiceRecipient.value.legal?.address;
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <AddressCompletionField
                            label={pathProxyField.label}
                            zipCode={address?.zipCode.value || ""}
                            city={address?.city.value || ""}
                            street={address?.street?.value}
                            complete={complete}
                            error={!!getFieldProps(`${pathProxyField.path}.error`).value}
                            onChange={(newValue) =>
                                setFieldValue(`${pathProxyField.path}.value`, newValue)
                            }
                        />
                    </Grid>
                );
            } else {
                const maxLength = new Map<string, number>(
                    Object.entries({
                        Vorname: 35,
                        Nachname: 35,
                        Name: 70,
                    }),
                ).get(pathProxyField.key);
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <LabledTextField
                            data-testid={pathProxyField.path}
                            label={pathProxyField.label}
                            style={{ width: "100%", flex: 1 }}
                            margin="normal"
                            variant="outlined"
                            inputProps={{ maxLength }}
                            error={!!getFieldValue(`${pathProxyField.path}.error`)}
                            helperText={getFieldValue(`${pathProxyField.path}.error`)}
                            {...getFieldProps(`${pathProxyField.path}.value`)}
                        />
                    </Grid>
                );
            }
        }
        if (salutationField) {
            return (
                <Grid item md={6 as GridSize} sm={12} xs={12} style={{ height: 56 }}>
                    <InputLabel className={classes.labledTextfieldLabel}>Anrede *</InputLabel>
                    <Select
                        style={{ width: "100%", flex: 1 }}
                        value={getSalutationFromChangeSet()}
                        onChange={onChangeSalutation}
                        error={
                            values.invoiceRecipient.error !== null &&
                            values.invoiceRecipient.error?.indexOf("salutation") !== -1
                        }
                    >
                        {[{ key: "", label: "Bitte wählen", type: "natural" }]
                            .concat(salutations)
                            .map((salutation) => (
                                <MenuItem value={salutation.key} key={salutation.key}>
                                    {salutation.label}
                                </MenuItem>
                            ))}
                    </Select>
                </Grid>
            );
        }
        if (fillerField) {
            return <Grid item md={fillerField.width as GridSize} sm={12} xs={12} />;
        }

        return <Fragment />;
    };

    const renderFields = (fields: Fields): JSX.Element => {
        return (
            <Fragment>{Object.keys(fields).map((field) => renderField(fields[field]))}</Fragment>
        );
    };

    const copyBuildingAddress = (): void => {
        const street = get(values, "buildingAddress.street.value") || "";
        const houseNumber = get(values, "buildingAddress.houseNumber.value") || "";
        const zipCode = get(values, "buildingAddress.zipCode.value") || "";
        const city = get(values, "buildingAddress.city.value") || "";

        if (isNatural && values.invoiceRecipient.value.natural) {
            setValues((values) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.natural!.address.street = {
                    value: street,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.natural!.address.houseNumber = {
                    value: houseNumber,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.natural!.address.zipCode = {
                    value: zipCode,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.natural!.address.city = {
                    value: city,
                };
            });
        } else if (!isNatural && values.invoiceRecipient.value.legal) {
            setValues((values) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.legal!.address.street = {
                    value: street,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.legal!.address.houseNumber = {
                    value: houseNumber,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.legal!.address.zipCode = {
                    value: zipCode,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.invoiceRecipient.value.legal!.address.city = {
                    value: city,
                };
            });
        }
    };

    if (!isInitialized) {
        setSalutation(getSalutationFromChangeSet());
        setIsInitialized(true);
    }

    return (
        <Fragment>
            <div className={classes.container}>
                <div className={classes.subTitle}>Auftraggeber / Rechnungsanschrift</div>
                <div className={classes.text}>
                    Hier erfassen Sie die persönlichen Daten des Auftraggebers.
                </div>
                <Grid container spacing={2}>
                    {renderField(fields.salutation)}
                    <Grid item md={6 as GridSize} sm={12} xs={12}>
                        <Button
                            mode="secondary"
                            style={{
                                marginTop: 34,
                                width: "100%",
                            }}
                            onClick={copyBuildingAddress}
                        >
                            Bauadresse übernehmen
                        </Button>
                    </Grid>
                    {isNatural ? renderFields(naturalFields) : renderFields(legalFields)}
                </Grid>
                <div className={classes.disclaimerContainer}>
                    <p>* Mit Sternchen gekennzeichnete Felder sind Pflichtfelder.</p>
                </div>
            </div>
        </Fragment>
    );
};
